import React, { useEffect, useRef, useState } from "react";

import { Typography, TypographyProps } from "@mui/material";

import { addThousandsSeparator } from "../../../utils/numberUtils";

export interface AnimatedNumberProps extends Omit<TypographyProps, "children"> {
  /** The number to display */
  value: number;
  /** Duration of the scale effect in milliseconds */
  scaleDuration?: number;
  /** Suffix to add after the number (e.g., "€") */
  suffix?: string;
}

const AnimatedNumber = ({
  value,
  scaleDuration = 2000,
  suffix = "",
  sx,
  ...props
}: AnimatedNumberProps) => {
  const [showScale, setShowScale] = useState(false);
  const previousValue = useRef(value);

  useEffect(() => {
    // Only show scale when value increases
    if (
      value > 0 &&
      previousValue.current > 0 &&
      value > previousValue.current
    ) {
      setShowScale(true);
      const timer = setTimeout(() => setShowScale(false), scaleDuration);

      previousValue.current = value;
      return () => clearTimeout(timer);
    }
    previousValue.current = value;
  }, [value, scaleDuration]);

  return (
    <Typography
      {...props}
      sx={{
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        ...(showScale && {
          transform: "scale(1.15)"
        }),
        ...sx
      }}
    >
      {value > 0 ? `${addThousandsSeparator(value)}${suffix}` : "-"}
    </Typography>
  );
};

export default AnimatedNumber;
